import type { NuxtApp } from 'nuxt/app';
import type { RuntimeConfig } from 'nuxt/schema';
import type { WebLocale } from '@/enums/web-locale';

import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';
import { getUserLocationCountryCode } from '@/helpers/geo-location-helper';
import { generateJwId } from '@/helpers/uuid-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { globalRoutes } from '@/routing/global';
import { detectDomain, Domain } from '@/routing/helpers/subdomain-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

export const JWID_COOKIE = 'jw_id' as const;
const MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 Days

export default defineNuxtPlugin({
	name: 'core-env',
	dependsOn: ['pinia-init'],
	parallel: false,
	async setup() {
		const runtimeConfig = useRuntimeConfig();
		const headers = useRequestHeaders();
		const context = useNuxtApp();
		const url = useRequestURL().pathname;
		const userStore = useUserStore(context.$pinia);
		const languageStore = useLanguageStore(context.$pinia);
		const routingStore = useRoutingStore(context.$pinia);

		let s = getHrTime();
		const didGenerateId = initJwId(runtimeConfig.public.jwConfig.GRAPHQL_URL, runtimeConfig.public.jwConfig.DOMAIN);
		recordSsrPhase(context, 'init-jw-id', s);

		if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
			// Make basic SSR init before init Store
			let currentXServeTrack = null;

			if (import.meta.server) {
				// get canary flag, e.g. canary-alpha if present
				// Header takes priority; fall back to X_SERVE_TRACK env so cached SSR
				// responses carry the deployment's track when the proxy header is absent.
				currentXServeTrack = headers['x-serve-track'] || runtimeConfig.xServeTrack || null;
			}

			context.provide('ssrData', {
				isSeoUser: didGenerateId,
				xServeTrack: currentXServeTrack,
			});
		}

		s = getHrTime();
		initWebLocale({
			headers,
			languageStore,
			url,
			userStore,
		});
		recordSsrPhase(context, 'domain-detection', s);

		initDomain({
			headers,
			nuxtApp: context,
			routingStore,
			runtimeConfig,
		});

		context.provide('webLocale', languageStore.webLocale);
		// Make current domain available to all Vue components for SmartLink domain detection
		context.provide('domain', routingStore.activeDomain);
	},
});

/**
 * Generates a valid JustWatch ID if needed and sets
 * it in a cookie for the rest of the app to use.
 *
 * Returns true a JW ID was generated. Meaning this
 * is an SEO user.
 */
function initJwId(graphqlUrl: string, domain: string): boolean {
	if (import.meta.server) {
		const jwIdCookie = useCookie(JWID_COOKIE);

		if (jwIdCookie.value) return false;

		const userCookie =
			useCookie<Record<string, unknown>>('jw_user').value || useCookie<Record<string, unknown>>('jw/user').value;

		if (!userCookie) return true;

		return !userCookie?.jwLoginId && !userCookie?.jwId;
	}

	if (document.cookie.includes(JWID_COOKIE)) return false;

	const jwId = generateJwId();

	fetch(graphqlUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			query: `mutation RegisterDeviceId { registerDeviceId(input: "${jwId}") { deviceId } }`,
		}),
	})
		.then(resp => (resp?.ok ? null : resp.json()))
		.then(json => {
			const error = (json?.errors ?? [])[0];
			if (error) throw error;
		})
		.catch(error => {
			captureMessageForSentry(
				'[Fetch GraphQL RegisterDeviceId]:',
				{ error, where: '[main.common.ts]: initJwId' },
				'error'
			);
		});

	// Delete any existing subdomain-specific jw_id cookie first
	document.cookie = `${JWID_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; SameSite=None`;

	// Set domain to share jw_id cookie across all subdomains (www, guides, sports, etc.)
	// In development, omit domain parameter for localhost compatibility
	const domainParam = process.env.NODE_ENV === 'development' ? '' : `.${domain}`;

	document.cookie =
		`${JWID_COOKIE}=${jwId}` +
		`; expires=${new Date(+new Date() + MAX_AGE).toUTCString()}` +
		`; path=/; secure; SameSite=None; Domain=${domainParam}`;

	return true;
}

function initDomain({
	headers,
	nuxtApp,
	routingStore,
	runtimeConfig,
}: {
	nuxtApp: NuxtApp;
	headers: Readonly<Record<string, string>>;
	runtimeConfig: RuntimeConfig;
	routingStore: ReturnType<typeof useRoutingStore>;
}) {
	const req = nuxtApp.ssrContext?.event?.node?.req;

	if (import.meta.server && req) {
		let hostname = headers.site || headers.host || headers['x-forwarded-host'] || headers['x-original-host'] || '';

		if (!hostname && req.url) {
			if (headers.referer) {
				try {
					const refUrl = new URL(headers.referer);
					hostname = refUrl.hostname;
				} catch (e) {}
			}

			// Fallback: check if we're being crawled by a bot that might include domain info in User-Agent or other headers
			if (!hostname) {
				const userAgent = headers['user-agent'] || '';

				if (userAgent.toLowerCase().includes('bot')) {
					// Check if referer contains any known subdomain pattern
					const referer = headers.referer || '';
					const knownSubdomains = Object.values(Domain).filter(domain => domain !== Domain.MAIN);

					// Look for any known subdomain in the referer
					const matchedSubdomain = knownSubdomains.find(subdomain => referer.includes(`${subdomain}.`));

					if (matchedSubdomain) {
						hostname = `${matchedSubdomain}.${runtimeConfig.public.jwConfig.DOMAIN}`;
					} else {
						hostname = `www.${runtimeConfig.public.jwConfig.DOMAIN}`;
					}
				}
			}
		}

		routingStore.setActiveDomain(detectDomain(hostname as string));
	} else if (import.meta.client) {
		routingStore.setActiveDomain(detectDomain(window.location.hostname));
	}
}

function initWebLocale({
	url,
	languageStore,
	userStore,
	headers,
}: {
	url: string;
	languageStore: ReturnType<typeof useLanguageStore>;
	userStore: ReturnType<typeof useUserStore>;
	headers: Readonly<Record<string, string>>;
}) {
	const path = url.split('?')[0]!;
	const slug = /^\/[\w\d]+/.exec(url)?.[0] || '';
	const isGlobalRoute = globalRoutes.includes(path) || globalRoutes.includes(slug);

	if (isGlobalRoute) {
		let locale =
			userStore.persistedWebLocale || ((import.meta.client && languageStore.detectedWebLocale) as string);

		// For Global routes we still need to know the location in the server
		// if server OR if SPA app
		if (!locale && import.meta.server) {
			// This header is only meant for global routes because the region is not in the URL
			// IN SSR, DO NOT PASS THIS TO CLIENT (use `getUserLocationCountryCode` instead)
			const region = headers['x-client-geo-region'];
			locale = region?.toLowerCase() as WebLocale;
		}

		// get Browser Locale as last resort
		// happens for SPA visitors that do not pass through SSR first
		if (!locale && import.meta.client) {
			locale = getUserLocationCountryCode()?.toLowerCase() as WebLocale;
		}

		// hard fallback to US just in case
		if (!locale) locale = 'us';

		languageStore.setDetectedWebLocale(locale);
	} else {
		const locale = path && path.length >= 3 && path.split('/')[1];

		const webLocale = locale && COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;

		languageStore.setDetectedWebLocale(webLocale as WebLocale);
	}
}
